"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Loader2Icon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { ApiError, apiFetch } from "@/lib/api";
import { formatDateTime, statusBadgeVariant } from "@/lib/format";
import type { OrderPublic } from "@/lib/types";

const RESEND_COOLDOWN_SECONDS = 30;
const HISTORY_TOKEN_STORAGE_KEY = "ps-order-history-token";

type HistoryStatus = "checking" | "email" | "sending" | "code_sent" | "loading" | "loaded";

export default function OrderLookupPage() {
  const router = useRouter();
  const [orderNumber, setOrderNumber] = useState("");

  function onOrderNumberSubmit(event: React.FormEvent) {
    event.preventDefault();
    const normalized = orderNumber.trim().toUpperCase();
    if (normalized) router.push(`/order-confirmation/${normalized}`);
  }

  const [historyStatus, setHistoryStatus] = useState<HistoryStatus>("checking");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [orders, setOrders] = useState<OrderPublic[] | null>(null);
  const [cooldown, setCooldown] = useState(0);

  // On load, try the stored "stay logged in" token before asking for a code again.
  useEffect(() => {
    let cancelled = false;

    async function restoreSession() {
      const token = localStorage.getItem(HISTORY_TOKEN_STORAGE_KEY);
      if (!token) {
        if (!cancelled) setHistoryStatus("email");
        return;
      }
      try {
        const result = await apiFetch<{ email: string; orders: OrderPublic[] }>("/orders/history/token/", {
          method: "POST",
          body: JSON.stringify({ token }),
        });
        if (cancelled) return;
        setEmail(result.email);
        setOrders(result.orders);
        setHistoryStatus("loaded");
      } catch {
        localStorage.removeItem(HISTORY_TOKEN_STORAGE_KEY);
        if (!cancelled) setHistoryStatus("email");
      }
    }

    restoreSession();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setInterval(() => setCooldown((prev) => Math.max(0, prev - 1)), 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  async function sendCode() {
    setHistoryError(null);
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setHistoryError("Enter a valid email");
      return;
    }
    setHistoryStatus("sending");
    try {
      await apiFetch("/orders/verify-email/request/", {
        method: "POST",
        body: JSON.stringify({ email: email.trim() }),
      });
      setHistoryStatus("code_sent");
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (error) {
      setHistoryStatus("email");
      setHistoryError(error instanceof ApiError ? error.message : "Could not send the code. Please try again.");
    }
  }

  async function viewOrders() {
    setHistoryError(null);
    setHistoryStatus("loading");
    try {
      const result = await apiFetch<{ email: string; token: string; orders: OrderPublic[] }>("/orders/history/", {
        method: "POST",
        body: JSON.stringify({ email: email.trim(), code }),
      });
      localStorage.setItem(HISTORY_TOKEN_STORAGE_KEY, result.token);
      setEmail(result.email);
      setOrders(result.orders);
      setHistoryStatus("loaded");
    } catch (error) {
      setHistoryStatus("code_sent");
      setHistoryError(error instanceof ApiError ? error.message : "That code is invalid or has expired.");
    }
  }

  function logOut() {
    localStorage.removeItem(HISTORY_TOKEN_STORAGE_KEY);
    setHistoryStatus("email");
    setEmail("");
    setCode("");
    setOrders(null);
    setHistoryError(null);
    setCooldown(0);
  }

  const loggedIn = historyStatus === "loaded";

  return (
    <main className="mx-auto flex w-full max-w-xl flex-1 flex-col justify-center gap-8 px-6 py-16">
      <Card>
        <CardHeader>
          <h1 className="font-heading text-2xl font-bold">Your Order History</h1>
          <p className="text-sm text-muted-foreground">
            {loggedIn ? "Every order placed with this email." : "We'll email you a code to confirm it's you."}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {historyStatus === "checking" && (
            <p className="text-sm text-muted-foreground">Checking for a saved session&hellip;</p>
          )}

          {(historyStatus === "email" || historyStatus === "sending" || historyStatus === "code_sent" || historyStatus === "loading") && (
            <>
              <div className="space-y-2">
                <Label htmlFor="historyEmail">Email</Label>
                <div className="flex gap-2">
                  <Input
                    id="historyEmail"
                    type="email"
                    value={email}
                    disabled={historyStatus === "sending" || historyStatus === "code_sent" || historyStatus === "loading"}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                  {historyStatus === "email" || historyStatus === "sending" ? (
                    <Button type="button" variant="outline" className="shrink-0" disabled={historyStatus === "sending"} onClick={sendCode}>
                      {historyStatus === "sending" ? <Loader2Icon className="animate-spin" /> : "Send code"}
                    </Button>
                  ) : (
                    <Button type="button" variant="outline" className="shrink-0" disabled={cooldown > 0} onClick={sendCode}>
                      {cooldown > 0 ? `Resend (${cooldown}s)` : "Resend code"}
                    </Button>
                  )}
                </div>
              </div>

              {(historyStatus === "code_sent" || historyStatus === "loading") && (
                <div className="space-y-2">
                  <Label htmlFor="historyCode">Verification code</Label>
                  <div className="flex gap-2">
                    <Input
                      id="historyCode"
                      type="text"
                      inputMode="numeric"
                      placeholder="6-digit code"
                      maxLength={6}
                      value={code}
                      onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
                    />
                    <Button
                      type="button"
                      className="shrink-0"
                      disabled={code.length !== 6 || historyStatus === "loading"}
                      onClick={viewOrders}
                    >
                      {historyStatus === "loading" ? <Loader2Icon className="animate-spin" /> : "View My Orders"}
                    </Button>
                  </div>
                </div>
              )}

              {historyError && <p className="text-sm text-destructive">{historyError}</p>}
            </>
          )}

          {loggedIn && orders && (
            <div className="space-y-4">
              {orders.length === 0 ? (
                <p className="text-sm text-muted-foreground">No orders found for this email.</p>
              ) : (
                <div className="space-y-3">
                  {orders.map((order) => (
                    <Link
                      key={order.order_number}
                      href={`/order-confirmation/${order.order_number}`}
                      className="block rounded-lg border border-border p-4 transition-colors hover:bg-muted"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                        <span className="font-mono text-base font-semibold">{order.order_number}</span>
                        <Badge variant={statusBadgeVariant(order.status)}>{order.display_status}</Badge>
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-sm text-muted-foreground">
                        <span>{formatDateTime(order.created_at)}</span>
                        <span className="tabular-nums">${order.total}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
              <Separator />
              <p className="text-sm text-muted-foreground">Signed in as {email}.</p>
              <Button type="button" variant="outline" className="w-full" onClick={logOut}>
                Log Out
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {!loggedIn && historyStatus !== "checking" && (
        <Card>
          <CardHeader>
            <h2 className="font-heading text-lg font-semibold text-muted-foreground">Looking for a single order?</h2>
            <p className="text-sm text-muted-foreground">
              Enter the order number from your confirmation, like ABCD-1234.
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={onOrderNumberSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="orderNumber">Order number</Label>
                <Input
                  id="orderNumber"
                  placeholder="ABCD-1234"
                  value={orderNumber}
                  onChange={(event) => setOrderNumber(event.target.value)}
                  className="uppercase"
                />
              </div>
              <Button type="submit" variant="outline" className="w-full">
                Look Up Order
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
    </main>
  );
}
